import { useState, useEffect, useCallback } from 'react'
import {
  getPackMeta,
  OFFLINE_PACK_META_UPDATED_EVENT,
  type PackMeta,
} from '../lib/offlineDb'
import {
  ensureOfflineReadiness,
  OFFLINE_PACK_REFRESH_STARTED_EVENT,
  OFFLINE_PACK_REFRESH_FINISHED_EVENT,
} from '../lib/offlineReadiness'

/**
 * useOfflinePack — pack metadata + manual refresh control.
 *
 * Phase 5 (2026-06-21): `downloadPack` now routes through the unified
 * orchestrator (`ensureOfflineReadiness({ force: true })`) instead of
 * the legacy `/offline-packs/download` route. Same surface, same UI —
 * the underlying server call is the new `/offline-readiness/fill`.
 * After the orchestrator's background fill completes, it writes
 * `PackMeta` via `savePackMeta` so the age display picks up the new
 * timestamp on the next read.
 *
 * Kept under the original name for backward-compat with consuming
 * components (OfflinePackCard, ManageDownloadsView). Suitable for a
 * rename to `useOfflineReadinessStatus` once those components migrate.
 */
export function useOfflinePack() {
  const [packMeta, setPackMeta] = useState<PackMeta | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load pack metadata from IDB on mount
  useEffect(() => {
    const refresh = () => { getPackMeta().then(setPackMeta).catch(() => {}) }
    const onRefreshStarted = () => setDownloading(true)
    const onRefreshFinished = () => setDownloading(false)
    refresh()
    window.addEventListener(OFFLINE_PACK_META_UPDATED_EVENT, refresh)
    window.addEventListener(OFFLINE_PACK_REFRESH_STARTED_EVENT, onRefreshStarted)
    window.addEventListener(OFFLINE_PACK_REFRESH_FINISHED_EVENT, onRefreshFinished)
    return () => {
      window.removeEventListener(OFFLINE_PACK_META_UPDATED_EVENT, refresh)
      window.removeEventListener(OFFLINE_PACK_REFRESH_STARTED_EVENT, onRefreshStarted)
      window.removeEventListener(OFFLINE_PACK_REFRESH_FINISHED_EVENT, onRefreshFinished)
    }
  }, [])

  const hasPack = packMeta !== null

  // Compute pack age in hours
  const packAgeHours = packMeta
    ? Math.floor((Date.now() - new Date(packMeta.downloadedAt).getTime()) / (1000 * 60 * 60))
    : 0

  const packAgeText = !packMeta
    ? ''
    : packAgeHours < 1
      ? 'Agora mesmo'
      : packAgeHours < 24
        ? `Há ${packAgeHours}h`
        : `Há ${Math.floor(packAgeHours / 24)} dia${Math.floor(packAgeHours / 24) > 1 ? 's' : ''}`

  const downloadPack = useCallback(async () => {
    setDownloading(true)
    setError(null)
    try {
      // The orchestrator's background path writes PackMeta on success.
      // Mode falls back to 'admission' because the caller didn't pass
      // one; this matches the legacy hook's default. ManageDownloadsView
      // is admission-targeted today; revisit when 12ª gets its own pack
      // UI.
      await ensureOfflineReadiness({
        priority: 'background',
        mode: 'admission',
        force: true,
      })
      const meta = await getPackMeta()
      setPackMeta(meta)
    } catch (err: any) {
      const msg = err?.data?.message ?? err?.data?.error ?? err?.message ?? 'Erro ao descarregar pack'
      setError(msg)
    } finally {
      setDownloading(false)
    }
  }, [])

  return { packMeta, hasPack, packAgeHours, packAgeText, downloading, error, downloadPack }
}
